import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { Button, Modal, SimpleGrid, TextInput } from "@mantine/core";
import { useDispatch, useSelector } from "react-redux";
import { STATE_CURRENCY, STATE_SALE_ID } from "../../Actions/cookiesActions";
import { addDiscount } from "../../Actions/articleActions";
import DiscountAlert from "../DiscountAlert";
import autoAnimate from "@formkit/auto-animate";
import {
  ADD_GIFT_CERTIFICATE,
  AppContext,
  SET_IDENTIFIER,
  SET_PAYMENT_METHOD,
} from "../../context";
import { useLazyQuery } from "@apollo/client";
import gql from "graphql-tag";
import { GIFTCERTIFICATE } from "../../Components/Payments/constants";
import { notification } from "../../Components/notification";

const query = gql`
  query giftCertificateQuery($identifier: String, $currency: String) {
    giftCertificateByIdentifier(identifier: $identifier, currency: $currency) {
      id
      availableAmount
      availableAmountInCurrency
    }
  }
`;

const reducer = (state, action) => {
  switch (action.type) {
    case "id":
      return { ...state, id: action.payload };
    case "info":
      return { ...state, info: action.payload };
    default:
      throw new Error("Ange bara kod eller info");
  }
};

const calculatePrice = (price1, price2) => {
  const currencySymbols = {
    $: 1,
    "€": 1,
    "£": 1,
    kr: 1,
  };

  const symbol1 = Object.keys(currencySymbols).find((symbol) =>
    price1.includes(symbol)
  );

  if (!symbol1) {
    throw new Error("Invalid currency symbol");
  }

  const price1Value = parseFloat(
    price1.replace(symbol1, "").replace(",", ".").trim()
  );

  const difference = (price1Value - price2).toFixed(2);
  return difference.toString().replace(".", ",");
};

const AddDiscountGiftCertificate = ({ classes, sale, getActiveMethods }) => {
  const [state, localDispatch] = useReducer(reducer, { id: "", info: "" });
  const [value, updateValue] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      opened: false,
      giftCardCode: "",
    }
  );
  const { config, language, saleId, currency } = useSelector((state) => ({
    currency: state.cookies[STATE_CURRENCY],
    config: state.config,
    language: state.language,
    saleId: state.cookies[STATE_SALE_ID],
  }));
  const context = useContext(AppContext);
  const [identifier, identifierSet] = useState(null);
  const rdxDispatch = useDispatch();
  const ctxDispatch = context.dispatch;
  const { giftCertificate } = context.state;
  const [giftcardData, setGiftcardData] = useState({});
  const [showRemovedText, setShowRemovedText] = useState(false);
  const discountParent = useRef(null);
  const giftCardParent = useRef(null);

  const hideLabel = {
    position: "absolute",
    width: "0px",
    height: "0px",
    opacity: 0,
  };

  const [getGiftCertificate] = useLazyQuery(query, {
    variables: { identifier: identifier, currency },
    fetchPolicy: "no-cache",
    onCompleted: (data) => setGiftcardData(data),
  });

  const emptyGiftCertificate =
    giftcardData?.giftCertificateByIdentifier?.[0]?.availableAmount === 0;

  const setPaymentMethod = (id) => {
    ctxDispatch({ type: SET_IDENTIFIER, payload: value.giftCardCode });
    ctxDispatch({ type: SET_PAYMENT_METHOD, payload: id });
  };

  let useZipCodeInDiscount = true;
  if (config !== undefined && config?.useZipCodeInDiscount === false) {
    useZipCodeInDiscount = false;
  } else {
  }

  const disabled = () => {
    if (state.id.length < 1) return true;

    if (useZipCodeInDiscount && (state.info < 10000 || state.info > 99999))
      return true;

    return false;
  };

  const submit = () => {
    rdxDispatch(addDiscount(saleId, state.id, state.info));
    updateValue({ opened: false });
  };

  const getGiftCard = useMemo(() => {
    const saleFilter = sale.payments.filter(
      (el) => el.paymentMethodName === "Presentkort"
    )?.[0];
    if (saleFilter) {
      return saleFilter;
    }
  }, [sale]);

  useEffect(() => {
    discountParent.current && autoAnimate(discountParent.current);
    giftCardParent.current && autoAnimate(giftCardParent.current);
  }, [discountParent, giftCardParent]);

  useEffect(() => {
    if (identifier) {
      getGiftCertificate();
    }
  }, [identifier]);

  useEffect(() => {
    if (!giftCertificate) {
      identifierSet("");
    }
  }, [giftCertificate]);

  const total = useCallback(
    (price) => {
      if (currency === "SEK") return `${price} kr`;
      if (currency === "DKK") return `${price} kr`;
      if (currency === "NOK") return `${price} kr`;
      if (currency === "GBP") return `£${price}`;
      if (currency === "USD") return `$${price}`;
      if (currency === "EUR") return `€${price}`;
    },
    [giftcardData, currency]
  );

  useEffect(() => {
    if (
      !emptyGiftCertificate &&
      giftcardData?.giftCertificateByIdentifier?.length > 0
    ) {
      const check = parseFloat(
        calculatePrice(
          sale.sumInclVat,
          giftcardData?.giftCertificateByIdentifier?.[0]
            ?.availableAmountInCurrency / 100
        ).replace(",", ".")
      );
      let giftCertificate = giftcardData?.giftCertificateByIdentifier?.[0];
      giftCertificate.coverTotalCost = check > 0 ? false : true;
      ctxDispatch({
        type: ADD_GIFT_CERTIFICATE,
        payload: giftCertificate,
      });
      setPaymentMethod(GIFTCERTIFICATE);
    } else if (
      identifier !== "" &&
      (emptyGiftCertificate ||
        giftcardData?.giftCertificateByIdentifier === null)
    ) {
      identifierSet("");
      notification("error", language.GiftCertificateCheckCode);
    }
  }, [giftcardData]);

  const giftCertificateMethodIsActive = getActiveMethods.find(
    (method) => method.id === GIFTCERTIFICATE
  );

  if (config?.hideGiftCertificateAndDiscountField) return null;
  return (
    <>
      <div className={classes.step}>
        <div
          className={classes.stepWrapper}
          style={{ gap: giftCertificateMethodIsActive ? "0 1.5rem" : 0 }}
        >
          {!config?.hideDiscount && (
            <div className={classes.textWrapper}>
              <div className={classes.inputWrapper}>
                <TextInput
                  className={classes.input}
                  value={
                    sale.discounts.length > 0
                      ? sale.discounts?.[0]?.id
                      : state.id
                  }
                  onChange={(e) =>
                    localDispatch({ type: "id", payload: e.target.value })
                  }
                  label={language.Receipt_DiscountCode}
                  disabled={sale.discounts.length > 0}
                />
                <Button
                  className={`${classes.addButton} primaryButton`}
                  size="sm"
                  disabled={state.id === "" || sale.discounts.length > 0}
                  variant={"primary"}
                  onClick={() => updateValue({ opened: true })}
                  aria-label={language.Discount_Add_Discount}
                  style={{ width: "7rem" }}
                >
                  {language.Discount_Submit_Discount}
                </Button>
              </div>
            </div>
          )}

          {giftCertificateMethodIsActive && (
            <div className={classes.textWrapper}>
              <div className={classes.inputWrapper}>
                <TextInput
                  className={classes.input}
                  value={value.giftCardCode}
                  onChange={(e) =>
                    updateValue({ giftCardCode: e.target.value })
                  }
                  label={language.GiftCertificate}
                  disabled={giftCertificate}
                />
                <Button
                  className={`${classes.addButton} primaryButton`}
                  size="sm"
                  disabled={giftCertificate || value.giftCardCode === ""}
                  variant={"primary"}
                  onClick={() => {
                    identifierSet(value.giftCardCode);
                  }}
                  aria-label={`${language.Discount_Submit_Discount} ${language.GiftCertificate}`}
                  style={{ width: "7rem" }}
                >
                  {language.Discount_Submit_Discount}
                </Button>
              </div>
            </div>
          )}
          <div ref={discountParent} role="region" aria-live="assertive">
            <div style={hideLabel}>
              {showRemovedText && (
                <div role="alert" aria-atomic={true}>
                  {showRemovedText ? language.Removed : ""}
                </div>
              )}
            </div>
            {sale.discounts.length > 0 && (
              <DiscountAlert
                sale={sale}
                color={"green"}
                title={language.Discount_Added}
                text={`${sale.discounts?.[0]?.id} - ${sale.discounts?.[0]?.discountInclVat}`}
                giftCard={false}
                tooltip={`${
                  language.Cart_RemoveRow
                } ${language?.Receipt_DiscountCode?.toLowerCase()}`}
                setShowRemovedText={setShowRemovedText}
              />
            )}
          </div>

          <div ref={giftCardParent} role="region" aria-live="assertive">
            {(getGiftCard || context?.state?.giftCertificate) &&
              giftCertificateMethodIsActive && (
                <DiscountAlert
                  sale={sale}
                  color={"green"}
                  title={language.GiftCertificate_Added}
                  text={`${
                    getGiftCard
                      ? getGiftCard.amount
                      : total(
                          context?.state?.giftCertificate
                            ?.availableAmountInCurrency / 100
                        )
                  }`}
                  giftCard={true}
                  tooltip={`${
                    language.Cart_RemoveRow
                  } ${language?.GiftCertificate?.toLowerCase()}`}
                  setShowRemovedText={setShowRemovedText}
                />
              )}
          </div>
        </div>
      </div>
      <Modal
        centered
        opened={value.opened}
        onClose={() => updateValue({ opened: false })}
        title={language.Receipt_DiscountCode}
        sx={{ title: { fontWeight: 500 } }}
        closeButtonProps={{ label: language.Close }}
      >
        <SimpleGrid cols={1}>
          <TextInput
            label={language.Discount_Description}
            placeholder="12345"
            value={state.info}
            onChange={(e) =>
              localDispatch({ type: "info", payload: e.target.value })
            }
            autoComplete="postal-code"
          />
          <Button
            className={"primaryButton"}
            size="md"
            onClick={submit}
            disabled={disabled()}
            aria-label={language.Discount_Add_Discount}
          >
            {language.Discount_Submit_Discount}
          </Button>
        </SimpleGrid>
      </Modal>
    </>
  );
};

export default AddDiscountGiftCertificate;
